import _ from "lodash";
import * as Elements from "./elements.js";

function init(wpd, wpdDocument, dataEntryVM) {
	wpd.barGraphEdit = {};

	let calibrator = null;

	const elementIds = {
		editBarGraphSidebar: "edit-bar-graph-sidebar",
		editBarGraphAdjustBarAxisPointsSidebar:
			"edit-bar-graph-adjust-bar-axis-points-sidebar",
		editBarGraphAdjustCategoricalAxisPointsSidebar:
			"edit-bar-graph-adjust-categorical-axis-points-sidebar",
		editBarGraphSetBarAxisValuesDialog:
			"edit-bar-graph-set-bar-axis-values-dialog",
		editBarGraphSetBarAxisDatatypesDialog: "bar-graph-axes-label-popup",
		editBarGraphAddCategorySidebar: "edit-bar-graph-add-category-dialog",
		editBarGraphRemoveCategorySidebar: "edit-bar-graph-remove-category-dialog",
	};

	Elements.apply(wpd, wpdDocument, elementIds);

	const unselectPoints = function () {
		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();
		if (metadata.barHeaders) {
			metadata.barHeaders.p1.isSelected = false;
			metadata.barHeaders.p2.isSelected = false;
		}

		axis.calibration.unselectAll();
		axis.setMetadata(metadata);

		calibrator = new wpd.BarAxesCalibrator(axis.calibration, true);
		calibrator.reload();
	};

	const resetRepainter = function () {
		const axis = wpd.tree.getActiveAxes();
		const plotData = wpd.appData.getPlotData();
		const datasets = plotData.getDatasetsForAxis(axis);
		const axes = Array(datasets.length).fill(axis);
		wpd.graphicsWidget.setRepainter(
			new wpd.MultipltDatasetRepainter(axes, datasets),
		);
	};

	wpd.barGraphEdit.adjustBarAxisPoints = function () {
		unselectPoints();
		const axis = wpd.tree.getActiveAxes();

		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.resetData();

		wpd.graphicsWidget.setTool(new wpd.AxesCornersTool(axis.calibration, true));
		wpd.graphicsWidget.setRepainter(
			new wpd.AlignmentCornersRepainter(axis.calibration),
		);
		wpd.graphicsWidget.forceHandlerRepaint();

		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphAdjustBarAxisPointsSidebar);
	};

	wpd.barGraphEdit.completeAdjustBarAxisPoints = function () {
		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();

		wpd.barGraphEdit.updateGraphPointsWithAxisPointValues();

		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.resetData();

		wpd.graphicsWidget.setTool(new wpd.BarHeadersTool(axis, dataEntryVM, true));

		resetRepainter();
		wpd.barGraphEdit.rebuildDividersAndHeaders(axis, metadata);
		wpd.graphicsWidget.forceHandlerRepaint();

		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphSidebar);
	};

	wpd.barGraphEdit.adjustCategoricalAxisPoints = function () {
		unselectPoints();
		const axis = wpd.tree.getActiveAxes();

		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.resetData();

		wpd.graphicsWidget.setTool(new wpd.BarCornersTool(axis));
		wpd.graphicsWidget.forceHandlerRepaint();

		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphAdjustCategoricalAxisPointsSidebar);
	};

	wpd.barGraphEdit.completeAdjustCategoricalAxisPoints = function () {
		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();

		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.resetData();

		wpd.graphicsWidget.setTool(new wpd.BarHeadersTool(axis, dataEntryVM, true));
		resetRepainter();
		wpd.barGraphEdit.rebuildDividersAndHeaders(axis, metadata);
		wpd.graphicsWidget.forceHandlerRepaint();

		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphSidebar);
	};

	wpd.barGraphEdit.rebuildDividersAndHeaders = function (axis, metadata) {
		wpd.barChartUtils.drawAxisLine(
			wpd.graphicsWidget.getAllContexts().dataCtx,
			axis,
			metadata,
		);
		wpd.barChartUtils.buildHeaderDividers(axis, metadata, true);
		wpd.barChartUtils.drawHeaderDividers(
			wpd.graphicsWidget.getAllContexts().dataCtx,
			metadata.barHeaders.headerDividers,
		);
		wpd.barChartUtils.buildHeaderAreas(axis, metadata, true);
		wpd.barChartUtils.drawHeaderGradients(
			metadata,
			wpd.graphicsWidget.getAllContexts().dataCtx,
		);
	};

	wpd.barGraphEdit.openSetBarAxisValuesDialog = function () {
		setValueFieldValues();
		wpd.popup.show(elementIds.editBarGraphSetBarAxisValuesDialog);
	};

	wpd.barGraphEdit.completeSetBarAxisValues = function () {
		const axis = wpd.tree.getActiveAxes();
		const p1ValueBefore = axis.calibration.getPoint(0).dy;
		const p2ValueBefore = axis.calibration.getPoint(1).dy;
		const isLogBefore = axis.isLog();
		const isRotatedBefore = axis.isRotated();
		const validResult = saveAxisPointValues();
		if (!validResult) {
			return;
		}

		const p1ValueAfter = axis.calibration.getPoint(0).dy;
		const p2ValueAfter = axis.calibration.getPoint(1).dy;
		const isLogAfter = axis.isLog();
		const isRotatedAfter = axis.isRotated();

		// Recalculate point values when the p1/p2 axis values change, or when the
		// log-scale or rotation setting changes. Toggling log or rotation re-maps
		// every pixel to a different value even if the calibration point values
		// are unchanged, so the stored values must be re-derived.
		if (
			p1ValueBefore !== p1ValueAfter ||
			p2ValueBefore !== p2ValueAfter ||
			isLogBefore !== isLogAfter ||
			isRotatedBefore !== isRotatedAfter
		) {
			wpd.barGraphEdit.updateGraphPointsWithAxisPointValues();
		}

		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.setTool(new wpd.BarHeadersTool(axis, dataEntryVM, true));
		wpd.popup.close(elementIds.editBarGraphSetBarAxisValuesDialog);
	};

	wpd.barGraphEdit.cancelSetBarAxisValues = function () {
		hideError("bar-edit-log-validation-message");
		wpd.popup.close(elementIds.editBarGraphSetBarAxisValuesDialog);
	};

	wpd.barGraphEdit.openSetBarAxisDatatypesDialog = function () {
		setDataTypeFieldValues();

		wpdDocument
			.getElementById("bar-graph-datatype-buttons")
			.setAttribute("hidden", "true");
		wpdDocument
			.getElementById("edit-bar-graph-datatype-buttons")
			.removeAttribute("hidden");

		wpd.alignAxes.initializeBarGraphFields();
		wpd.popup.show(elementIds.editBarGraphSetBarAxisDatatypesDialog);
	};

	wpd.barGraphEdit.completeSetBarAxisDatatypes = async function () {
		const axis = wpd.tree.getActiveAxes();
		const barDecimalsBefore = axis.getMetadata().axesLabelData.bar.decimals;
		const dataTypes = wpd.alignAxes.saveAxesData(false, false, true);

		const barDecimalsAfter = dataTypes.barAxisDecimals;

		wpd.barGraphEdit.updateGraphPointsWithAxisDataTypes(axis, dataTypes);

		await wpd.alignAxes.cascadeBroadcastTagsChange(
			axis,
			dataTypes.broadcastTagsBefore,
			dataTypes.broadcastTagsAfter,
		);

		if (barDecimalsBefore !== barDecimalsAfter) {
			wpd.barGraphEdit.updateGraphPointsWithAxisPointValues(
				null,
				null,
				null,
				null,
				true,
			);
		}

		wpd.graphicsWidget.removeTool();
		wpd.tree.editAxis(axis.name);
	};

	wpd.barGraphEdit.cancelSetBarAxisDatatypes = function () {
		wpd.popup.close(elementIds.editBarGraphSetBarAxisDatatypesDialog);
		wpd.pointGroups.resetSettings();
	};

	wpd.barGraphEdit.addCategory = function () {
		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphAddCategorySidebar);

		const axis = wpd.tree.getActiveAxes();
		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.resetData();
		wpd.graphicsWidget.forceHandlerRepaint();
		wpd.graphicsWidget.setTool(new wpd.AddBarHeaderTool(axis, dataEntryVM));
	};

	wpd.barGraphEdit.cancelAddCategory = function () {
		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphSidebar);

		const axis = wpd.tree.getActiveAxes();
		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.resetData();
		wpd.graphicsWidget.forceHandlerRepaint();
		wpd.graphicsWidget.setTool(new wpd.BarHeadersTool(axis, dataEntryVM, true));
	};

	wpd.barGraphEdit.removeCategory = function () {
		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphRemoveCategorySidebar);

		const axis = wpd.tree.getActiveAxes();
		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.resetData();
		wpd.graphicsWidget.forceHandlerRepaint();

		wpd.graphicsWidget.setTool(new wpd.RemoveBarHeaderTool(axis, dataEntryVM));
	};

	wpd.barGraphEdit.cancelRemoveCategory = function () {
		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.editBarGraphSidebar);

		const axis = wpd.tree.getActiveAxes();
		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.resetData();
		wpd.graphicsWidget.forceHandlerRepaint();
		wpd.graphicsWidget.setTool(new wpd.BarHeadersTool(axis, dataEntryVM, true));
	};

	wpd.barGraphEdit.openSetStudyBranchesDialog = function () {
		wpd.dataSeriesManagement.showAddDataset(true);
		setStudyBranchFieldValues();

		// build per-branch broadcast groups from the rows just populated, then
		// fill in any saved broadcast entries from axesLabelData
		wpd.dataSeriesManagement.syncBranchBroadcastSection();
		const axes = wpd.tree.getActiveAxes();
		const existing = axes?.getMetadata?.()?.axesLabelData?.broadcastTags;
		wpd.dataSeriesManagement.populateBranchBroadcastTags(existing);
	};

	wpd.barGraphEdit.closeBarGraphEdit = function () {
		const axis = wpd.tree.getActiveAxes();
		const calibration = axis.calibration;
		calibration.unselectAll();

		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.resetData();
		wpd.graphicsWidget.forceHandlerRepaint();
		wpd.sidebar.clear();
	};

	const setStudyBranchFieldValues = function () {
		let axis = wpd.tree.getActiveAxes();
		const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
		if (_.isEmpty(datasets)) {
			return;
		}

		for (let i = 0; i < datasets.length - 1; i++) {
			wpd.dataSeriesManagement.addRow(false, false);
		}
		const rowContainer = wpdDocument.getElementById("axis-study-branch-inputs");

		const rows = Array.from(rowContainer.children);
		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			const ds = datasets[i];
			const md = ds.getMetadata();

			const dsInput = row.querySelector(".add-dataset-study-arm-input");
			const tgInput = row.querySelector(".add-dataset-tag-group-input");

			dsInput.setAttribute("data-composite-key", md.studyData.compositeKey);
			dsInput.value = wpd.CompositeKeyUtils.getCompositeKeyValue(
				md.studyData.compositeKey,
			);
			tgInput.value = md.studyData.tagGroup.name;
			// align row's dataset uuid with the existing dataset so edit-mode
			// rows reuse the committed identity instead of the freshly minted one
			row.dataset.datasetUuid = md.uuid;

			if (datasets[i].getAllPixels().length > 0) {
				dsInput.disabled = true;
				tgInput.disabled = true;
				const removeButton = row.querySelector(".dataset-remove-button");

				if (removeButton) {
					removeButton.disabled = true;
				}

				const errorContainer = row.querySelector(".uneditable-message");
				errorContainer.innerText =
					"This study branch has data points and cannot be edited/removed from this tool; use the edit/remove buttons in the Graph Data window instead";
			}
		}
	};

	const saveAxisPointValues = function () {
		hideError("bar-edit-log-validation-message");
		const values = {
			min: wpdDocument.getElementById("edit-bar-axes-p1").value,
			max: wpdDocument.getElementById("edit-bar-axes-p2").value,
			log: wpdDocument.getElementById("edit-bar-axes-log-scale").checked,
			rotated: wpdDocument.getElementById("edit-bar-axes-rotated").checked,
		};

		if (
			values.log &&
			(parseFloat(values.min) <= 0 || parseFloat(values.max) <= 0)
		) {
			showError("bar-edit-log-validation-message");
			return false;
		}

		let axis = wpd.tree.getActiveAxes();
		let calibration = axis.calibration;
		calibration.setDataAt(0, 0, values.min);
		calibration.setDataAt(1, 0, values.max);
		axis.calibrate(calibration, values.log, values.rotated);
		return true;
	};

	const hideError = function (errorClass) {
		wpdDocument
			.getElementById("edit-bar-graph-set-bar-axis-values-dialog")
			.querySelector(`.${errorClass}`)
			.setAttribute("hidden", "true");
	};

	const showError = function (errorClass) {
		wpdDocument
			.getElementById("edit-bar-graph-set-bar-axis-values-dialog")
			.querySelector(`.${errorClass}`)
			.removeAttribute("hidden");
	};

	const setValueFieldValues = function () {
		const axis = wpd.tree.getActiveAxes();
		const calibration = axis.calibration;
		wpdDocument.getElementById("edit-bar-axes-p1").value =
			calibration.getPoint(0).dy;
		wpdDocument.getElementById("edit-bar-axes-p2").value =
			calibration.getPoint(1).dy;
		wpdDocument.getElementById("edit-bar-axes-log-scale").checked =
			axis.isLog();
		wpdDocument.getElementById("edit-bar-axes-rotated").checked =
			axis.isRotated();
	};

	const setDataTypeFieldValues = function () {
		const axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();
		const barData = metadata.axesLabelData.bar;
		wpdDocument.getElementById("bar-axis-data-type").value = barData.dataType;
		wpdDocument.getElementById("bar-axis-unit").value = barData.unit;
		wpdDocument.getElementById("bar-axis-description").value =
			barData.description ?? "";
		// initialize dropdowns and load saved values
		wpd.utils.initializeRatioTypesSelect(
			wpdDocument.getElementById("bar-axis-change-from-baseline"),
			barData.changeFromBaseline,
		);

		wpdDocument.getElementById("bar-axis-decimals").value = barData.decimals;

		// load point group
		wpd.pointGroups.loadFromSavedSettings(axis);

		// Populate the broadcast-tags section. Awesompletes for these rows are
		// created inside populateBroadcastTags, so this must run AFTER any
		// destroy/init of the axis-data-type awesompletes.
		wpd.alignAxes.populateBroadcastTags(
			metadata.axesLabelData?.broadcastTags || [],
			false,
			"bar",
		);
	};

	wpd.barGraphEdit.updateGraphPointsWithAxisDataTypes = function (
		axis,
		dataTypes,
	) {
		const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
		const changeFromBaselineTag = _.find(wpd.tagGroups.tags, {
			name: "Change from baseline",
		});
		const descriptionTag = _.find(wpd.tagGroups.tags, {
			name: "Description",
		});
		const valueUnitTag = wpd.tagGroups.findTag({
			name: "Unit",
		});
		const timeUnitTag = wpd.tagGroups.findTag({
			name: "Time (unit)",
		});

		const metadata = axis.getMetadata();
		const currentRotation = wpd.graphicsWidget.getRotation();
		const swapAxes = currentRotation && currentRotation % 180 !== 0;
		const initialRotation = metadata.initialRotation;
		const rotationDifference = Math.abs(currentRotation - initialRotation);
		const swapXY = rotationDifference > 0 && rotationDifference % 180 !== 0;

		let barDirection =
			metadata.barHeaders.barOrientation === "VERTICAL"
				? swapAxes
					? "x"
					: "y"
				: swapAxes
				? "y"
				: "x";

		if (swapXY) {
			barDirection = barDirection === "x" ? "y" : "x";
		}

		datasets.forEach((ds) => {
			const pixels = ds.getAllPixels();
			pixels.forEach((px, index) => {
				let maxIndex = 0;
				let reindex = false;
				let hasDescription = false;
				let hasChangeFromBaseline = false;
				let hasUnit = false;
				let filteredMetadataKeys = Object.keys(px.metadata).filter(
					(key) => !isNaN(key),
				);

				if (filteredMetadataKeys.length === 0) {
					// need to calculate axis type via axis key and tag
					filteredMetadataKeys = Object.keys(px.metadata).filter(
						(key) => !isNaN(key) && !_.isNil(px.metadata[key].axis),
					);

					filteredMetadataKeys.forEach((key) => {
						switch (px.metadata[key].tag.uuid) {
							case changeFromBaselineTag.uuid:
								px.metadata[key].axisType = "changeFromBaseline";
								break;
							case valueUnitTag.uuid:
								px.metadata[key].axisType = "unit";
								break;
							case timeUnitTag.uuid:
								px.metadata[key].axisType = "unit";
								break;
							default:
								px.metadata[key].axisType = "value";
						}
					});
				}

				filteredMetadataKeys.forEach((key) => {
					const dp = px.metadata[key];
					maxIndex = Math.max(maxIndex, parseInt(key, 10));
					let isDelete = false;

					if (dp.axis !== barDirection) {
						return;
					}

					if (dp.axisType === "unit") {
						hasUnit = true;
						dp.tag =
							dataTypes.barAxisDataType === "Time (number)"
								? timeUnitTag
								: valueUnitTag;

						dp.value = dataTypes.barAxisUnit;
					} else if (dp.axisType === "value") {
						if (ds.hasPointGroups()) {
							let tag;
							if (dataEntryVM.pointGroupOverhaul) {
								tag = wpd.pointGroups.getDataPointPointGroupTag(
									ds,
									index,
									barDirection,
								);
							} else {
								tag = wpd.pointGroups.getDataPointPointGroupTagFromSettings(
									ds,
									barDirection,
									"bar",
									index,
								);
							}

							if (tag) {
								dp.tag = _.pick(tag, ["name", "uuid"]);
							} else {
								dp.tag = _.pick(
									_.find(wpd.tagGroups.tags, {
										name: dataTypes.barAxisDataType,
									}),
									["name", "uuid"],
								);
							}
						} else {
							dp.tag = _.pick(
								_.find(wpd.tagGroups.tags, {
									name: dataTypes.barAxisDataType,
								}),
								["name", "uuid"],
							);
						}

						dp.value = wpd.utils.roundAxisValue(
							dp.value,
							axis.isLog(),
							dataTypes.barAxisDecimals,
						);
					} else if (dp.axisType === "changeFromBaseline") {
						hasChangeFromBaseline = true;
						if (dataTypes.barAxisChangeFromBaseline) {
							dp.value = dataTypes.barAxisChangeFromBaseline;
						} else {
							isDelete = true;
							reindex = true;
							delete px.metadata[key];
						}
					} else if (dp.axisType === "description") {
						hasDescription = true;
						if (dataTypes.barAxisDescription) {
							dp.value = dataTypes.barAxisDescription;
						} else {
							isDelete = true;
							reindex = true;
							delete px.metadata[key];
						}
					}

					if (!isDelete) {
						px.metadata[key] = dp;
					}
				});

				if (dataTypes.barAxisDescription && !hasDescription) {
					px.metadata[++maxIndex] = {
						tag: descriptionTag,
						value: dataTypes.barAxisDescription,
						axis: barDirection,
						axisType: "description",
					};
				}

				if (dataTypes.barAxisChangeFromBaseline && !hasChangeFromBaseline) {
					px.metadata[++maxIndex] = {
						tag: changeFromBaselineTag,
						value: dataTypes.barAxisChangeFromBaseline,
						axis: barDirection,
						axisType: "changeFromBaseline",
					};
				}

				if (dataTypes.barAxisUnit && !hasUnit) {
					px.metadata[++maxIndex] = {
						tag:
							dataTypes.barAxisDataType === "Time (number)"
								? timeUnitTag
								: valueUnitTag,
						value: dataTypes.barAxisUnit,
						axis: barDirection,
						axisType: "unit",
					};
				}

				if (reindex) {
					wpd.custom.reindexPixelMetadata(px);
				}
			});

			// regenerate tuple data
			if (dataEntryVM.pointGroupOverhaul && ds.hasPointGroups()) {
				wpd.pointGroups
					.sortDataPointsByCenterFirst(axis, ds.getAllPixels())
					.forEach((px) => {
						let axisDirection = px.metadata.pointGroupAxisDirection;

						if (!axisDirection) {
							axisDirection = "center";
							px.metadata.pointGroupAxisDirection = axisDirection;
						}

						px.metadata.tuple = wpd.pointGroups.generateTupleData(
							ds,
							px,
							axisDirection,
						);
					});
			}
		});
	};

	const updateGraphPointWithAxisPointValues = function (
		axis,
		dataset,
		dataPoint,
		skipUpdateAxis,
		decimalOnly,
	) {
		const metadata = axis.getMetadata();
		const data = axis.pixelToData(dataPoint.x, dataPoint.y);
		let isUpdated = false;

		const currentRotation = wpd.graphicsWidget.getRotation();
		const swapAxes = currentRotation && currentRotation % 180 !== 0;
		const initialRotation = metadata.initialRotation;
		const rotationDifference = Math.abs(currentRotation - initialRotation);
		const swapXY = rotationDifference > 0 && rotationDifference % 180 !== 0;

		let barDirection =
			metadata.barHeaders.barOrientation === "VERTICAL"
				? swapAxes
					? "x"
					: "y"
				: swapAxes
				? "y"
				: "x";

		if (swapXY) {
			barDirection = barDirection === "x" ? "y" : "x";
		}

		Object.keys(dataPoint.metadata)
			.filter((key) => !isNaN(key))
			.forEach((key) => {
				const row = dataPoint.metadata[key];
				if (
					["x", "y"].includes(row.axis) &&
					row.axisType === "value" &&
					row.axis === barDirection &&
					skipUpdateAxis !== row.axis
				) {
					row.value = wpd.utils.roundAxisValue(
						decimalOnly ? row.value : data[0],
						axis.isLog(),
						metadata?.axesLabelData?.bar?.decimals ?? 2,
					);
					isUpdated = true;
				}
			});

		// If an update was made, regenerate the tuple id
		if (
			isUpdated &&
			dataEntryVM.pointGroupOverhaul &&
			dataset.hasPointGroups() &&
			dataPoint.metadata.pointGroupAxisDirection === "center"
		) {
			wpd.pointGroups.syncTupleIdsToCenterPoint(dataset, dataPoint);
		}
	};

	wpd.barGraphEdit.updateGraphPointsWithAxisPointValues = function (
		axis,
		dataset,
		pointIndexes,
		skipUpdateAxis,
		decimalOnly,
	) {
		if (!axis) {
			axis = wpd.tree.getActiveAxes();
		}

		let datasets;
		if (!dataset) {
			datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
		} else {
			datasets = [dataset];
		}

		datasets.forEach((ds) => {
			let dataPoints;
			if (pointIndexes) {
				dataPoints = pointIndexes.map((index) => ds.getPixel(index));
			} else {
				dataPoints = ds.getAllPixels();
			}

			dataPoints.forEach((dataPoint) => {
				updateGraphPointWithAxisPointValues(
					axis,
					ds,
					dataPoint,
					skipUpdateAxis,
					decimalOnly,
				);
			});
		});
	};

	/**
	 * Gets the header data for a data point.
	 *
	 * @param {Object} dataPoint - The data point to get the header data for
	 * @returns {Object} The header data for the data point
	 */
	wpd.barGraphEdit.getHeaderDataForDataPoint = (axis, dataPoint) => {
		const axisMetadata = axis.getMetadata();

		if (!axisMetadata.barHeaders) {
			return;
		}

		const headers = axisMetadata.barHeaders.headers;
		const barHeaders = axisMetadata.barHeaders;

		// determine header orientation
		const headerOrientation =
			barHeaders.headerOrientation === "HORIZONTAL" ? "x" : "y";

		const startKey = `start${headerOrientation.toUpperCase()}`;
		const endKey = `end${headerOrientation.toUpperCase()}`;

		const coordinate = dataPoint[headerOrientation];

		// find which header this data point belongs to
		let headerIndex = _.findIndex(headers, (header) => {
			return _.inRange(coordinate, header[startKey], header[endKey]);
		});

		// buildHeaderAreas insets each header rectangle by headerBarMargin, so
		// adjacent headers leave a ~2px dead zone straddling every divider (and
		// the outer edges sit just outside the first/last rectangle). A bar whose
		// categorical coordinate lands on a divider - common in cumulative-
		// distribution plots digitized right on the gridlines - then matches NO
		// header, which strips ALL categorical data (Description, study branch)
		// from that bar (DCTL-1833 / DHELP-204). Fall back to the nearest header
		// by distance to its span so an on-divider bar still inherits a category.
		// Only points that match no header are touched; any point already inside
		// a header keeps its exact assignment.
		if (headerIndex === -1 && headers?.length) {
			let nearestDistance = Infinity;
			headers.forEach((header, index) => {
				const low = Math.min(header[startKey], header[endKey]);
				const high = Math.max(header[startKey], header[endKey]);
				const distance =
					coordinate < low
						? low - coordinate
						: coordinate > high
						? coordinate - high
						: 0;
				if (distance < nearestDistance) {
					nearestDistance = distance;
					headerIndex = index;
				}
			});
		}

		if (headerIndex === -1) {
			return;
		}

		// prepare header data for insertion
		const headerData = _.chain(headers[headerIndex].metadata)
			.omitBy(wpd.utils.isWord)
			.keys()
			.reduce((result, key) => {
				result.push(headers[headerIndex].metadata[key]);
				return result;
			}, [])
			.value();

		return headerData;
	};

	/**
	 * Updates axis data points with bar header metadata.
	 *
	 * @param {Object} axis - The bar graph axis
	 */
	wpd.barGraphEdit.updateAxisDataPointsWithBarHeaderData = function (axis) {
		const axisMetadata = axis.getMetadata();

		if (!axisMetadata.barHeaders) {
			return;
		}

		const currentRotation = wpd.graphicsWidget.getRotation();
		const swapAxes = currentRotation && currentRotation % 180 !== 0;
		const initialRotation = axisMetadata.initialRotation;
		const rotationDifference = Math.abs(currentRotation - initialRotation);
		const swapXY = rotationDifference > 0 && rotationDifference % 180 !== 0;

		let [valueOrientation, headerOrientation] =
			axisMetadata.barHeaders.headerOrientation === "HORIZONTAL"
				? swapAxes
					? ["x", "y"]
					: ["y", "x"]
				: swapAxes
				? ["y", "x"]
				: ["x", "y"];

		if (swapXY) {
			headerOrientation = headerOrientation === "x" ? "y" : "x";
		}

		// The categorical (header) axis carries the same Description tag the
		// value axis does. Type it as "description" so it propagates to every
		// bar symmetric with the value-axis description (DCTL-1833 / DHELP-204);
		// every other header tag stays a plain categorical entry. This mirrors
		// the per-point stamping in user-script.js's bar point-add handler.
		const descriptionTag = _.find(wpd.tagGroups.tags, {
			name: "Description",
		});
		const headerAxisType = (headerDatum) =>
			descriptionTag && headerDatum.tag?.uuid === descriptionTag.uuid
				? "description"
				: undefined;

		const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
		datasets.forEach((dataset) => {
			const dataPoints = dataset.getAllPixels();
			dataPoints.forEach((dataPoint, index) => {
				const headerData = wpd.barGraphEdit.getHeaderDataForDataPoint(
					axis,
					dataPoint,
				);

				if (!headerData) {
					return;
				}

				// check if existing data point metadata has the header data
				// if so, update; otherwise, add
				let metadata = dataPoint.metadata;
				const metadataRowKeys = Object.keys(metadata).filter(
					(key) => !isNaN(key),
				);
				let maxIndex = metadataRowKeys.length;

				// Check if each header datum exists in the existing metadata
				// and update in place; otherwise, add a new row.
				//
				// Match by (tag.uuid, axis) rather than tag.uuid alone:
				// the same tag (e.g. Description) can legitimately appear
				// on both the value axis (from axesLabelData.bar.description)
				// and the header axis (from per-bar header metadata). Matching
				// on tag.uuid alone would let a header refresh stomp the
				// value-axis row, repointing it to the header axis and
				// destroying the configured value-axis description.
				headerData.forEach((headerDatum) => {
					const axisType = headerAxisType(headerDatum);
					let found = false;
					for (const key of metadataRowKeys) {
						const metadatum = metadata[key];

						if (
							metadatum.tag.uuid === headerDatum.tag.uuid &&
							metadatum.axis === headerOrientation
						) {
							metadatum.value = headerDatum.value;
							// Heal rows a prior point-add stamped as a plain "value":
							// re-type the categorical Description so it matches the
							// value-axis description row.
							if (axisType) {
								metadatum.axisType = axisType;
							}
							found = true;
							break;
						}
					}

					if (!found) {
						metadata[maxIndex++] = {
							...headerDatum,
							axis: headerOrientation,
							...(axisType ? { axisType } : {}),
						};
					}
				});

				// remove metadata that is no longer in the header data
				metadataRowKeys.forEach((key) => {
					const metadatum = metadata[key];
					if (
						metadatum.axis === headerOrientation &&
						!headerData.some(
							(headerDatum) => headerDatum.tag.uuid === metadatum.tag.uuid,
						)
					) {
						delete metadata[key];
					}
				});

				metadata = wpd.utils.reindexMetadata(metadata);

				dataset.setMetadataAt(index, metadata);
			});

			// Regenerate point-group tuple ids from geometry after every header
			// tag has been applied. We intentionally do NOT use
			// syncTupleIdsToCenterPoint here: it groups a center's sibling points
			// by their CURRENT tuple id, so two bars that already share a
			// colliding id (same category + same rounded center value) stay
			// merged no matter how their headers are relabeled -- the collision
			// perpetuates itself. Regenerating each point on its own instead
			// (centers from their own tags; bounds matched to their center by
			// x/y position inside generateTupleData) separates them.
			if (dataEntryVM.pointGroupOverhaul && dataset.hasPointGroups()) {
				dataset.getAllPixels().forEach((dp, dpIndex) => {
					const direction = dp.metadata.pointGroupAxisDirection;
					if (direction === "center") {
						dp.metadata.tuple = wpd.pointGroups.generateTupleData(
							dataset,
							dp,
							"center",
						);
					} else if (direction && dp.metadata.tuple?.[direction]) {
						const datum = wpd.pointGroups.generateTupleData(
							dataset,
							dp,
							direction,
							dp.metadata.tuple[direction].tagUuid,
						);
						if (datum?.[direction]?.id != null) {
							dp.metadata.tuple[direction].id = datum[direction].id;
						}
					}
					dataset.setMetadataAt(dpIndex, dp.metadata);
				});
			}
		});
	};
}

export { init };
